import { FC, Suspense } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy';
// components

const PdpRespHeroCarousel = dibsLazy('unavailablePdpCarouselRefactor-false', () =>
    import(
        /* webpackChunkName: "unavailablePdpCarouselRefactor-false" */ '../../PdpRespHeroCarousel/PdpRespHeroCarousel'
    ).then(mod => ({ default: mod.PdpRespHeroCarousel }))
);
const PdpRespSharedHeroCarousel = dibsLazy('unavailablePdpCarouselRefactor-true', () =>
    import(
        /* webpackChunkName: "unavailablePdpCarouselRefactor-true" */ '../../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel'
    ).then(mod => ({ default: mod.PdpRespSharedHeroCarousel }))
);

// types
import { PdpRespUnavailableHeroCarousel_item$key as Item } from './__generated__/PdpRespUnavailableHeroCarousel_item.graphql';
import { PdpRespUnavailableHeroCarousel_user$key as User } from './__generated__/PdpRespUnavailableHeroCarousel_user.graphql';
import { PdpRespUnavailableHeroCarousel_viewer$key as Viewer } from './__generated__/PdpRespUnavailableHeroCarousel_viewer.graphql';

const userFragment = graphql`
    fragment PdpRespUnavailableHeroCarousel_user on User {
        ...PdpRespHeroCarousel_user
        ...PdpRespSharedHeroCarousel_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespUnavailableHeroCarousel_item on Item {
        title
        firstPhotoWebPath(size: small)
        seller {
            isAccessible
        }
        ...PdpRespHeroCarousel_item
        ...PdpRespSharedHeroCarousel_item
    }
`;

const viewerFragment = graphql`
    fragment PdpRespUnavailableHeroCarousel_viewer on Viewer {
        unavailablePdpCarouselRefactor: featureFlag(feature: "unavailablePdpCarouselRefactor")
        ...PdpRespHeroCarousel_viewer
        ...PdpRespSharedHeroCarousel_viewer
    }
`;

export const PdpRespUnavailableHeroCarousel: FC<{
    item: Item;
    user: User | null;
    viewer: Viewer;
}> = ({ user: userRef, item: itemRef, viewer: viewerRef }) => {
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const environment = useRelayEnvironment();
    const { title = '', firstPhotoWebPath = '', seller } = item;
    const { relayVars, currency } = useRespUnavailableContext();
    const { userId, itemId, isTrade, fetchUser, afterDisplay: isClient } = relayVars;

    return (
        <Suspense fallback={null}>
            {viewer.unavailablePdpCarouselRefactor ? (
                <PdpRespSharedHeroCarousel
                    viewer={viewer}
                    item={item}
                    user={user}
                    currency={currency}
                    afterDisplay={isClient}
                    size="small"
                />
            ) : (
                <PdpRespHeroCarousel
                    user={user}
                    item={item}
                    hideBubbles
                    viewer={viewer}
                    itemTitle={title}
                    currency={currency}
                    environment={environment}
                    itemImage={firstPhotoWebPath}
                    relayVariables={{
                        userId,
                        itemId,
                        isTrade,
                        isClient,
                        fetchUser,
                    }}
                    disableRequestMoreImagesModal={!seller?.isAccessible}
                />
            )}
        </Suspense>
    );
};
